import { Check } from 'lucide-react';
import { AgentDot } from '../../../AgentDot';
import { useTranslation } from '../../../../lib/i18n';
import { type BandCardAgentData, type BandCardAppData } from '../../../../lib/band-cards';
import { ConnectorLogo } from '../../connectors/ConnectorLogo';
import { Typography } from '../../ui';
import styles from './BandAgentRows.module.css';

/**
 * A band's team: one row per agent, an initial tile with the name in the primary ink and what they own
 * in the secondary.
 *
 * Shared by both band cards so the team reads the same before and after the band exists — the offer and
 * the band it turned into are the same people, and two copies of this would let them drift apart.
 *
 * The dot is the shared `AgentDot`, the same concentric artwork the roster, the home map and Android's
 * own band card draw, so a teammate looks identical everywhere. It keys on the agent id once the band
 * exists. An OFFER has no ids, because those agents have not been hired yet, so it keys on the name
 * instead: still stable, still distinct per teammate, and the dot settles onto the real one the moment
 * the band is created.
 */
export function BandAgentRows({ agents }: { agents: BandCardAgentData[] }) {
  return (
    <ul className={styles.agents}>
      {agents.map((agent) => (
        <li key={agent.id ?? agent.name} className={styles.agent}>
          <AgentDot id={agent.id ?? agent.name} size={24} initial={agent.name.charAt(0)} />
          <span className={styles.text}>
            <Typography variant="p2" className={styles.name}>
              {agent.name}
            </Typography>
            {agent.title && (
              <Typography variant="p3" color="secondary" className={styles.title}>
                {agent.title}
              </Typography>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * The same team as ONE line: the dots side by side, then the names.
 *
 * The created band's card is a confirmation, not a directory — read once, under a reply — and four agents
 * at a row each made it the tallest thing in the chat while its connectors sat on a single line below. So
 * the card that says "this now exists" wears the compact form, and the OFFER, which is a proposal the
 * reader compares against another one, keeps the row-per-agent list where each title is legible. Same
 * `AgentDot` artwork and the same colours either way.
 */
export function BandAgentLine({
  agents,
  /**
   * Drawn without the dot cluster when the same teammates are already ON SCREEN as the orbit's nodes,
   * which is the created band card: the dots would be the same four marks twice, and the line is only
   * there to say which name belongs to whom. Defaults on, so every other caller is unchanged.
   */
  showDots = true,
}: {
  agents: BandCardAgentData[];
  showDots?: boolean;
}) {
  return (
    <div className={styles.line} data-testid="chat-band-agent-line">
      {showDots && (
        <span className={styles.lineDots}>
          {agents.map((agent) => (
            <AgentDot key={agent.id ?? agent.name} id={agent.id ?? agent.name} size={24} initial={agent.name.charAt(0)} />
          ))}
        </span>
      )}
      <Typography variant="p2" className={styles.lineNames}>
        {agents.map((agent) => agent.name).join(', ')}
      </Typography>
    </div>
  );
}

/**
 * One app NAMED beside its plate.
 *
 * A bare logo cluster is unreadable to someone meeting these apps for the first time: the two or three
 * famous marks carry themselves and every other app renders as an anonymous glyph, so the card asks the
 * owner to connect something it never names. Reported in the owner's own words — *"some app icons are
 * not well known, how will i know what apps you offer me to connect if i dont recognize the logo?"* —
 * with Gmail and Instagram legible while Google Calendar and Wix Stores were identical grey squares.
 *
 * The NAME is the content here and the plate is decoration, so they travel together everywhere an app is
 * listed: this replaces every bare-logo cluster on both band cards.
 *
 * `connected` draws the check, which is what lets a connect chain report itself in place rather than in
 * a second card underneath.
 *
 * Mirrors iOS `BandAppChip` and Android's twin.
 */
export function BandAppChip({ app, connected = false }: { app: BandCardAppData; connected?: boolean }) {
  const { t } = useTranslation();
  return (
    <span className={styles.chip} role="listitem" aria-label={connected ? `${app.name}, ${t('common.connected')}` : app.name}>
      {/* The plate treatment only — an offer's apps are none of them connected, and the chip says where
          an app stands with the check rather than by tinting its logo. */}
      <ConnectorLogo connector={{ toolkit: app.toolkit, logo: app.logoUrl, connected: true }} name={app.name} size={20} radius={6} glyphSize={13} />
      <Typography variant="p3" className={styles.chipName}>
        {app.name}
      </Typography>
      {connected && <Check size={13} strokeWidth={2.5} absoluteStrokeWidth aria-hidden className={styles.chipCheck} />}
    </span>
  );
}

/**
 * A cluster of named chips, WRAPPING — iOS's `FlowLayout` over `BandAppChip`.
 *
 * It lives here rather than being laid out twice at the two call sites, so the offer panel's apps and the
 * band card's connect checklist cannot end up spaced differently, and neither card can regress to a
 * single truncating row.
 */
export function BandAppChips({
  apps,
  /** Per-app, because the band card's cluster fills in with checks while the offer's apps have none. */
  isConnected,
  ariaLabel,
}: {
  apps: BandCardAppData[];
  isConnected?: (app: BandCardAppData) => boolean;
  ariaLabel?: string;
}) {
  return (
    <div className={styles.chips} role="list" aria-label={ariaLabel}>
      {apps.map((app) => (
        <BandAppChip key={app.toolkit} app={app} connected={isConnected?.(app) ?? false} />
      ))}
    </div>
  );
}
